// Custom hook for Credits
// Easy access to credit balance from any component

import { useState, useEffect } from 'react';
import { getCreditBalance, getRecentTransactions } from '../services/creditsService';
import { CreditBalance, CreditTransaction } from '../types/credits.types';

export function useCredits(userId: string | null) {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!userId) {
      setBalance(null);
      setTransactions([]);
      setLoading(false);
      return;
    }

    let mounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        const [balanceData, transactionsData] = await Promise.all([
          getCreditBalance(userId),
          getRecentTransactions(userId, 10)
        ]);
        
        if (mounted) {
          setBalance(balanceData);
          setTransactions(transactionsData);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to load credits'));
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [userId]);

  const refresh = async () => {
    if (!userId) return;
    
    try {
      const [balanceData, transactionsData] = await Promise.all([
        getCreditBalance(userId),
        getRecentTransactions(userId, 10)
      ]);
      
      setBalance(balanceData);
      setTransactions(transactionsData);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to refresh credits'));
    }
  };

  return {
    balance: balance?.balance ?? 0,
    lifetimeEarned: balance?.lifetimeEarned ?? 0,
    lifetimeSpent: balance?.lifetimeSpent ?? 0,
    transactions,
    loading,
    error,
    refresh
  };
}
